import _ from 'lodash';

import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import getNavigationFeatures from 'bundles/author-navigation/utils/navigationFeaturePermissions';
import { VERB } from 'bundles/authoring/common/utils/permissions/permissionFeatures';
import type {
  Feature,
  FeaturePermissions,
  InputFeatures,
  ScopedFeaturePermissions,
  ScopedFeatures,
  UserPermissions,
} from 'bundles/teach-course/utils/types';

import MemoizedPermissionsNetworkRequest from './memoizedPermissionsNetworkRequest';

export const PERMISSION = {
  GRANTED: 'GRANTED',
  FORBIDDEN: 'FORBIDDEN',
};

function isCustomVerb(verb: string): boolean {
  return ![VERB.CREATE, VERB.READ, VERB.UPDATE, VERB.DELETE, VERB.PUBLISH, VERB.RESTRICT].includes(verb);
}

function generateFeatureKey(name: string, verb: string, instanceId: string): string {
  return isCustomVerb(verb)
    ? `(instanceId~${instanceId},name~${name},verb~customVerb!~${verb})`
    : `(instanceId~${instanceId},name~${name},verb~${verb})`;
}

export function hasFeaturePermission(
  name: string,
  verb: string,
  instanceId: string,
  permissions: UserPermissions
): boolean {
  const featureKey = generateFeatureKey(name, verb, instanceId);
  return !!(permissions[featureKey] && permissions[featureKey] === PERMISSION.GRANTED);
}

// Returns true if featurePermissions do not exist (!featurePermissions), meaning no feature permissions
// were retrieved as course is not behind the feature permissions epic.
// Otherwise checks permissions on the input feature name and verb
export function hasAccess(name: string, verb: string, featurePermissions?: FeaturePermissions): boolean {
  return !featurePermissions || (featurePermissions[name] && featurePermissions[name][verb]);
}

export function generateFeaturePermissions(
  features: InputFeatures,
  permissions: UserPermissions
): ScopedFeaturePermissions {
  const featurePermissions: ScopedFeaturePermissions = {};
  _.each(features, (scopedFeatures: ScopedFeatures) => {
    featurePermissions[scopedFeatures.instanceId] = {};
    _.each(scopedFeatures.features, (feature: Feature) => {
      featurePermissions[scopedFeatures.instanceId][feature.name] = {};
      _.each(feature.verbs, (verb: string) => {
        featurePermissions[scopedFeatures.instanceId][feature.name][verb] = hasFeaturePermission(
          feature.name,
          verb,
          scopedFeatures.instanceId,
          permissions
        );
      });
    });
  });

  return featurePermissions;
}

const MemoizedPermissionsNetworkRequestSingleton = new MemoizedPermissionsNetworkRequest();

type PermissionAuthorizer = (features: FeaturePermissions) => boolean;

// TODO: replace routeAuthorizationPromise during react-router v4 migration
export async function routeAuthorizationPromise(
  userId: number,
  courseSlug: string,
  contextId?: string,
  authorizer?: PermissionAuthorizer,
  isContextLevelPermission?: boolean
): Promise<{ isAuthorized: boolean; error?: Error }> {
  try {
    /** Unless this permission is context-scoped, context ID should be ignored */
    const contextIdUnderConsideration: string | undefined =
      isContextLevelPermission && contextId ? contextId : undefined;
    const response = await MemoizedPermissionsNetworkRequestSingleton.getPermissions(`${userId}`, courseSlug);

    /** Follows format userId~courseId */
    const courseId = stringKeyToTuple(response.elements[0]?.id ?? '')?.[1];
    const navigationFeatures = getNavigationFeatures(courseId, contextIdUnderConsideration);
    const scopedFeaturePermissions = generateFeaturePermissions(navigationFeatures, response.elements[0].permissions);

    return {
      isAuthorized: !!(authorizer && authorizer(scopedFeaturePermissions[contextIdUnderConsideration || courseId])),
    };
  } catch (error) {
    return { isAuthorized: false, error };
  }
}
